import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isUserAdmin } from '../../../lib/supabase';
import { createUser, UserAdministrationError } from '../../../lib/userAdministration';

interface MentorFormData {
  email: string;
  name: string;
  specialization: string;
}

export function AddMentor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [formData, setFormData] = useState<MentorFormData>({
    email: '',
    name: '',
    specialization: ''
  });

  useEffect(() => {
    async function checkAdminStatus() {
      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);
      if (!adminStatus) {
        setError('You must be an admin to create mentors');
      }
    }
    checkAdminStatus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) {
      setError('You must be an admin to create mentors');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createUser({
        email: formData.email,
        name: formData.name,
        specialization: formData.specialization,
        role: 'MENTOR'
      });

      // Success! Navigate back to mentor list
      navigate('/coordinator/mentors');

    } catch (err) {
      console.error('Error in mentor creation:', err);
      if (err instanceof UserAdministrationError) {
        setError(`Failed to create mentor (${err.step}): ${err.message}`);
      } else {
        setError('An unexpected error occurred while creating the mentor');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Add New Mentor</h1>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-sm">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Full Name
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4CAF50] focus:outline-none focus:ring-[#4CAF50]"
            required
            disabled={!isAdmin}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4CAF50] focus:outline-none focus:ring-[#4CAF50]"
            required
            disabled={!isAdmin}
          />
        </div>

        <div>
          <label htmlFor="specialization" className="block text-sm font-medium text-gray-700">
            Specialization
          </label>
          <input
            type="text"
            id="specialization"
            value={formData.specialization}
            onChange={(e) => setFormData(prev => ({ ...prev, specialization: e.target.value }))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4CAF50] focus:outline-none focus:ring-[#4CAF50]"
            required
            disabled={!isAdmin}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !isAdmin}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#4CAF50] hover:bg-[#45a049] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4CAF50] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating Mentor...' : 'Create Mentor'}
        </button>
      </form>
    </div>
  );
} 